/**
 * Make form shortcodes visible in the preview, and only in the preview.
 *
 * ⚠ A SHORTCODE IS NOT MARKUP. `[gravityform id="1" …]` is an instruction to
 * WordPress, so the preview iframe — which is not WordPress — renders it as
 * literal text where the form should be. The page is correct and looks broken,
 * which is the worst combination: an operator reviewing a draft cannot tell
 * whether the form is wired up, mis-wired, or missing entirely.
 *
 * ⚠ THIS RUNS ON THE IFRAME'S srcDoc AND NOWHERE ELSE. Publishing re-assembles
 * the page server-side from the same request and never accepts HTML from the
 * browser, so a placeholder physically cannot reach a client's WordPress. If
 * that ever changes — if publish starts posting the HTML it was shown — this
 * function becomes a way to ship a grey box instead of a lead form, and it
 * must move behind the same seam the generator uses.
 */

interface Provider {
  pattern: RegExp;
  label: string;
}

const PROVIDERS: Provider[] = [
  { pattern: /\[gravityform[^\]]*\]/gi, label: 'Gravity Forms' },
  { pattern: /\[contact-form-7[^\]]*\]/gi, label: 'Contact Form 7' },
  { pattern: /\[wpforms[^\]]*\]/gi, label: 'WPForms' },
  { pattern: /\[fluentform[^\]]*\]/gi, label: 'Fluent Forms' },
  { pattern: /\[ninja_form[^\]]*\]/gi, label: 'Ninja Forms' },
  { pattern: /\[formidable[^\]]*\]/gi, label: 'Formidable' },
];

const ID_RE = /id=["']?(\d+)["']?/i;

// Sized to roughly a real form so the section's height is honest.
function placeholderBox(name: string, lead: string, note: string): string {
  return (
    '<div style="border:2px dashed #9aa4b2;border-radius:10px;padding:28px 20px;' +
    'text-align:center;color:#5b6675;font:500 14px/1.5 system-ui,sans-serif;' +
    'background:#f7f8fa">' +
    `<div style="font-weight:700;color:#3d4756">${name}</div>` +
    `<div style="margin-top:6px">${lead}</div>` +
    '<div style="margin-top:10px;font-size:12px;opacity:.75">' +
    note +
    '</div>' +
    '</div>'
  );
}

function placeholder(label: string, shortcode: string): string {
  const id = ID_RE.exec(shortcode)?.[1];
  return placeholderBox(
    id ? `${label} #${id}` : label,
    'renders here on the client&rsquo;s site',
    'Shown as a placeholder because this preview is not WordPress. The ' +
      'published page carries the shortcode itself.',
  );
}

/** Returns the html with any form shortcode replaced by a visible block. */
export function withVisibleForms(html: string): string {
  let out = html;
  for (const { pattern, label } of PROVIDERS) {
    out = out.replace(pattern, (match) => placeholder(label, match));
  }
  return out;
}

/**
 * ⚠ FAVORITE PREVIEWS ONLY, AND NOT THE DRAFT PREVIEW. The hosted providers
 * are not shortcodes: GoHighLevel ships an iframe to the client's own live
 * form plus its vendor script, so unlike the bracketed shortcodes above this
 * IS working markup and it renders a real, submittable lead form. In a draft
 * preview that is correct — the operator is reviewing the page that will
 * ship. In the favorites library it is not: browsing a saved section is not a
 * page view, and a submission from it would create a real lead in the
 * client's CRM out of a browsing action.
 *
 * Replaced rather than stripped, because a form band whose form is simply
 * gone previews as an empty hole and reads as a broken capture.
 */
const HOSTED_FORM_IFRAME =
  /<iframe[^>]*api\.leadconnectorhq\.com\/widget\/form\/([A-Za-z0-9_-]{12,})[^>]*>\s*<\/iframe>/gi;

const HOSTED_FORM_SCRIPT =
  /<script[^>]*link\.msgsndr\.com\/js\/form_embed\.js[^>]*>\s*<\/script>/gi;

export function withPlaceholderHostedForm(html: string): string {
  return html
    .replace(HOSTED_FORM_IFRAME, (_match, formId: string) =>
      placeholderBox(
        `GoHighLevel form ${formId}`,
        'the live lead form renders here on the client&rsquo;s page',
        'Shown as a placeholder because this is a library preview. The real ' +
          'form posts into the client&rsquo;s CRM, and browsing a saved ' +
          'section must not be able to create a lead.',
      ),
    )
    .replace(HOSTED_FORM_SCRIPT, '');
}

/** True when the html contains a form shortcode this module can display. */
export function hasFormShortcode(html: string): boolean {
  return PROVIDERS.some(({ pattern }) => {
    pattern.lastIndex = 0;
    return pattern.test(html);
  });
}


/**
 * Preview-only CSS injected into the iframe document.
 *
 * ⚠ THE SCROLLBAR IN THE PREVIEW BELONGS TO THE IFRAME, NOT THE PANE. The pane
 * is `overflow-hidden` and the frame fills it, so the rendered page scrolls
 * inside itself — correct behaviour that still puts a scrollbar track down the
 * middle of the screen, beside the one the app already has. The page stays
 * scrollable by wheel, keyboard and touch; only the chrome is hidden.
 *
 * ⚠ NEVER PUBLISHED. Publishing re-assembles the page server-side and does not
 * accept HTML from the browser, so this cannot reach a client's WordPress.
 */
const PREVIEW_CSS =
  '<style data-preview-only="1">' +
  'html{scrollbar-width:none;-ms-overflow-style:none}' +
  'html::-webkit-scrollbar{width:0;height:0;display:none}' +
  // ⚠ THE WHITE FRAME AROUND EVERY DRAFT WAS THE BROWSER'S, NOT OURS.
  // What goes into srcDoc is a FRAGMENT — no <html>, no <body> — so the
  // browser supplies its own, and its default stylesheet puts `margin: 8px`
  // on it. Measured in the preview iframe: bodyMargin "8px". Every draft an
  // operator has ever reviewed was matted in an 8px white border down all
  // four edges, and full-bleed bands (the brand-wave hero, the closing CTA)
  // stopped short of the edge they are designed to run off.
  //
  // The published page never had it: WordPress supplies the body. So the
  // preview was showing a frame that does not exist in the product, on the
  // one screen whose entire job is telling you what the product looks like.
  // Reported as "why are there white borders" (Jacob, 2026-08-23).
  'body{margin:0}' +
  '</style>';

/** The document actually handed to the preview iframe. */
export function toPreviewDocument(html: string): string {
  const withForms = withVisibleForms(html);
  // Prefer </head>; a generated page always has one, but fall back rather
  // than silently dropping the rule if the shape ever changes.
  if (withForms.includes('</head>')) {
    return withForms.replace('</head>', PREVIEW_CSS + '</head>');
  }
  return PREVIEW_CSS + withForms;
}
